import React from "react";
import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { useTranslation } from "../../localization/useTranslation";
import { useAnnualWheelActivityStandardTemplateList } from "./AnnualWheelActivityStandardTemplateList.hooks";
import {
    DotLegalButton,
    DotLegalDeleteDialog,
    DotLegalMultiSelect,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    GridSizes,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { AnnualWheelActivityStandardTemplateTableModel } from "./AnnualWheelActivityStandardTemplate.types";
import AnnualWheelActivityStandardTemplateDialog from "./AnnualWheelActivityStandardTemplateDialog.tsx";
import DotLegalTableWithControlsLocalized from "../../common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized.tsx";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import Grid from "@mui/material/Grid2";
import { Trans } from "react-i18next";
import { sortIds } from "../../common/utilities.ts";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

const AnnualWheelActivityStandardTemplateList: React.FC = () => {
    const {
        isLoading,
        getFilteredTemplates,
        breadCrumbs,
        onSaved,
        onCreateClick,
        createDialogOpen,
        onUpdateClick,
        onDialogClose,
        templateToUpdate,
        getMenuItems,
        templateForDeletion,
        setTemplateForDeletion,
        onDelete,
        annualWheelActivityGeneralTypesFilterOptions,
        searchedAnnualWheelActivityGeneralTypes,
        setSearchedAnnualWheelActivityGeneralTypes,
        searchedAreas,
        setSearchedAreas,
        areasFilterOptions,
        searchedStatus,
        setSearchedStatus,
    } = useAnnualWheelActivityStandardTemplateList();

    const { translateString, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const { platformFeatures: platform } = usePlanContext();

    const headers = Array<ITableHeader<AnnualWheelActivityStandardTemplateTableModel>>();
    headers.push({ property: "imageUrl", text: "" });
    headers.push({ property: "customId", text: translateString("id"), sortOverride: (a, b, order) => sortIds(a.customId, b.customId, order === "desc") });
    headers.push({ property: "name", text: translateString("name") });
    headers.push({ property: "description", text: translateString("description") });
    headers.push({ property: "statusString", text: translateString("status") });
    headers.push({ property: "complianceAreaString", text: translateString("area") });
    headers.push({ property: "generalType", text: translateString("type") });
    headers.push({ property: "notificationPeriodInDays", text: translateString("notificationDays") });
    headers.push({ property: "recurringIntervalString", text: translateString("recurrence") });

    function getSearchFields(): JSX.Element {
        const gridSizes: GridSizes = {
            xs: 6,
            sm: 4,
            md: 2,
            lg: 2,
        };
        const noOptionsLabel = translateString("noOptions");
        return (
            <React.Fragment>
                <Grid size={{ ...gridSizes }}>
                    {platform.annualWheelComplianceArea && (
                        <DotLegalMultiSelect
                            options={areasFilterOptions}
                            chosenOptions={searchedAreas}
                            onChange={setSearchedAreas}
                            label={translateString("area")}
                            placeholder={translateString("area")}
                            isLoading={isLoading}
                            noMargin
                            limit={1}
                            noOptionsLabel={noOptionsLabel}
                        />
                    )}
                </Grid>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        options={annualWheelActivityGeneralTypesFilterOptions}
                        chosenOptions={searchedAnnualWheelActivityGeneralTypes}
                        onChange={setSearchedAnnualWheelActivityGeneralTypes}
                        placeholder={translateString("type")}
                        label={translateString("type")}
                        noOptionsLabel={translateString("noOptions")}
                        noMargin
                    />
                </Grid>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        options={[
                            { id: "1", name: translateString("active") },
                            { id: "0", name: translateString("archived") },
                        ]}
                        chosenOptions={searchedStatus}
                        onChange={setSearchedStatus}
                        placeholder={translateString("status")}
                        label={translateString("status")}
                        noOptionsLabel={translateString("noOptions")}
                        noMargin
                    />
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    <DotLegalButton buttonType={"primary"} size="medium" onClick={onCreateClick}>
                        Create
                    </DotLegalButton>
                }
            />
            <TablePagePaper>
                <DotLegalTableWithControlsLocalized
                    headers={headers}
                    isLoading={isLoading}
                    internalScroll
                    data={getFilteredTemplates()}
                    defaultOrderBy={"name"}
                    hasOverflowMenu
                    extraControls={getSearchFields()}
                    getUserSpecificPageLength={() => 100}
                    renderRow={(row) => (
                        <TableRow key={row.id} inactive={row.archived} onClick={() => onUpdateClick(row.id)}>
                            <TableCell>
                                {isNullOrWhitespace(row.imageUrl) ? undefined : (
                                    <Box sx={{ display: "flex" }}>
                                        <Box height={24} width={24} component={"img"} src={row.imageUrl} />
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell>{row.customId}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                                {isNullOrWhitespace(row.description) ? undefined : <MentionInputRender value={row.description ?? ""} linkColor={"white"} />}
                            </TableCell>
                            <TableCell>{row.statusString}</TableCell>
                            <TableCell>{row.complianceAreaString}</TableCell>
                            <TableCell>{row.generalType && translateAnnualWheelActivityGeneralTypeEnum(row.generalType.toString())}</TableCell>
                            <TableCell>{row.notificationPeriodInDays}</TableCell>
                            <TableCell>{row.recurringIntervalString}</TableCell>
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>
            {createDialogOpen && <AnnualWheelActivityStandardTemplateDialog onCancel={onDialogClose} onSaved={onSaved} />}
            {templateToUpdate && <AnnualWheelActivityStandardTemplateDialog onCancel={onDialogClose} onSaved={onSaved} model={templateToUpdate} />}
            {templateForDeletion && (
                <DotLegalDeleteDialog
                    headerText={translateString("archive") + " " + templateForDeletion.name}
                    alertBodyText={<Trans i18nKey="archiveConfirmation" values={{ item: templateForDeletion.name }}></Trans>}
                    cancelBtnText={translateString("cancel")}
                    deleteBtnText={translateString("archive")}
                    warningTranslation={translateString("warning")}
                    onDialogClose={() => {
                        setTemplateForDeletion(undefined);
                    }}
                    open
                    onOkClick={onDelete}
                />
            )}
        </Box>
    );
};

export default AnnualWheelActivityStandardTemplateList;
